/**
 * Audit Dashboard (#579).
 *
 * Renders the per-tenant activity summary, recent events with filters,
 * and a CSV export action. Tenant scoping is enforced server-side from
 * the JWT — the UI never sends tenant_id.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import ActivityErrorBanner from '@components/activity/ActivityErrorBanner';
import AuditEventsTable from '@components/activity/audit/AuditEventsTable';
import AuditFilters from '@components/activity/audit/AuditFilters';
import AuditSummaryPanel from '@components/activity/audit/AuditSummaryPanel';
import PeriodSelect from '@components/activity/audit/PeriodSelect';
import {
  ActivityPeriod,
  ActivityResult,
  ActivitySummary,
  AuditEventPage,
  activityService,
} from '@services/activity.service';
import { useAppSelector } from '@store/index';
import { selectUser } from '@features/auth/authSlice';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';

const AuditDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  // CSV export endpoint is admin-only server-side. Manager users can
  // reach this page for the read-only summary/event view, but rendering
  // the export button to them produces a guaranteed 403 — so we hide it
  // when the role is not admin.
  const user = useAppSelector(selectUser);
  const userRoles = React.useMemo(() => {
    const roles: string[] = [];
    if (user?.role) roles.push(user.role);
    if (Array.isArray(user?.roles)) {
      user.roles.forEach((r) => {
        if (!roles.includes(r)) roles.push(r);
      });
    }
    return roles;
  }, [user]);
  const canExport = userRoles.includes('admin');

  const [period, setPeriod] = useState<ActivityPeriod>('7d');
  const [resultFilter, setResultFilter] = useState<'' | ActivityResult>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [page, setPage] = useState<AuditEventPage | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    setError(null);
    try {
      const data = await activityService.getSummary(period);
      setSummary(data);
    } catch (e) {
      setError(t('audit.summaryFetchFailed', { message: resolveApiErrorMessage(t, e) }));
    } finally {
      setLoadingSummary(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the fetch-error message; excluded so a language change doesn't refetch the summary (PR #790)
  }, [period]);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    setError(null);
    try {
      const data = await activityService.listEvents({
        result: resultFilter || undefined,
        action_category: categoryFilter || undefined,
        limit: 100,
      });
      setPage(data);
    } catch (e) {
      setError(t('audit.eventsFetchFailed', { message: resolveApiErrorMessage(t, e) }));
    } finally {
      setLoadingEvents(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the fetch-error message; excluded so a language change doesn't refetch events (PR #790)
  }, [resultFilter, categoryFilter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const onExportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await activityService.exportEventsCsv({
        result: resultFilter || undefined,
        action_category: categoryFilter || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(t('audit.csvExportFailed', { message: resolveApiErrorMessage(t, e) }));
    } finally {
      setExporting(false);
    }
  }, [resultFilter, categoryFilter, t]);

  const categories = useMemo(
    () => summary?.by_category ?? [],
    [summary],
  );

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h4 className="revy-cjk text-2xl font-semibold leading-[1.4] tracking-tight">
          {t('audit.title')}
        </h4>
        <div className="flex items-center gap-2">
          <span className="revy-cjk text-sm leading-[1.6]">{t('audit.period')}:</span>
          <PeriodSelect value={period} onChange={setPeriod} />
        </div>
      </div>

      {error && <ActivityErrorBanner message={error} />}

      {loadingSummary || !summary ? (
        <div className="my-8 flex justify-center">
          <Spinner />
        </div>
      ) : (
        <AuditSummaryPanel summary={summary} categories={categories} />
      )}

      <Card className="p-4">
        <AuditFilters
          resultFilter={resultFilter}
          categoryFilter={categoryFilter}
          categories={categories}
          canExport={canExport}
          exporting={exporting}
          onResultChange={setResultFilter}
          onCategoryChange={setCategoryFilter}
          onExportCsv={onExportCsv}
        />
      </Card>

      <AuditEventsTable rows={page?.items ?? []} loading={loadingEvents} />
    </div>
  );
};

export default AuditDashboardPage;
